import * as React from 'react';
import { connect } from 'react-redux';
import { Prompt } from 'react-router'
import { Link, RouteComponentProps } from 'react-router-dom';
import { ApplicationState } from '../../../store';
import * as DataModels from '../../../store/Models/SharedMaterials/DataModel';
import { ToxnotReactComponentPaths } from '../../core/ToxnotReactComponentPaths';
import * as Utilities from '../../../utilities/Utilities';
import { ReportTypesManager } from '../../../store/Managers/ReportTypesManager';
import { UpsellProBadge } from '../../core/Upsell';
import { ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { ToxnotSignInRequiredComponent } from '../../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../../core/ToxnotReactModal';
import PickReportsForCompanyModal from '../PickReportsForCompanyModal';
import { ReportContext } from '../../../store/Models/Customers/Customer';

import './CompanyPageEditor.css';
import UpdateCompanyRequest from '../../../store/Models/Api/UpdateCompanyRequest';

// ===============================================================================

export enum CustomUrlNameState {
    Disabled,
    Checking,
    Valid,
    Invalid,
    OpenToEdit
}

enum PublishingState {
    Default,
    Published,
    Publishing
}

// ===============================================================================

interface CompanyPageEditorState {
    companyLogoFileObjectUrl: string | null;
    companyLogoFile: File | null;
    updatedCompany: DataModels.SharedMaterialsEditableCompany;
    isBeingEdited: boolean;
    customUrlNameState: CustomUrlNameState;
    publishingState: PublishingState;
}

// ===============================================================================

interface VerificationBundle {
    updatedCompany: DataModels.SharedMaterialsEditableCompany;
    localizedErrorMsg: string | null;
    focusSelectorId: string | null;
}

// ===============================================================================

export interface PublishingOptions {
    fileToUpload: File | null
}

// ===============================================================================

interface CompanyPageEditorOwnProps {
    initialCompany: DataModels.SharedMaterialsEditableCompany;
    onCompanyBeingEdited: (isBeingEdited: boolean) => void;
    onCompanyPagePublish: (updateCompany: DataModels.SharedMaterialsEditableCompany, publishedSuccessfully: boolean) => void;
}

// ===============================================================================

type CompanyPageEditorProps =
    ToxnotComponentProperties &
    RouteComponentProps &
    CompanyPageEditorOwnProps;

// ===============================================================================

class CompanyPageEditor extends ToxnotSignInRequiredComponent<CompanyPageEditorProps, CompanyPageEditorState>{

    private _serviceClient: ToxnotServiceClient;

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    constructor(props: CompanyPageEditorProps) {
        super(props);

        this._serviceClient = new ToxnotServiceClient();

        this.state = {
            companyLogoFileObjectUrl: null,
            companyLogoFile: null,
            updatedCompany: this.getCopyOfInitalCompanyPage(),
            isBeingEdited: false,
            customUrlNameState: CustomUrlNameState.Disabled,
            publishingState: PublishingState.Default
        };
    }

    // ===============================================================================

    public componentWillUnmount() {
        this.cleanUpLogoObjectUrl();
    }

    // ===============================================================================

    private getUrlToExchange(ownerId: string, companyId: string, fullUrl?: boolean) {
        const suffix = `${ToxnotReactComponentPaths.SharedMaterialSupplierBase}/${ownerId}/${companyId}`;

        if (!fullUrl) {
            return suffix
        }

        return Utilities.addhostnameToString(suffix);
    }

    // ===============================================================================

    private getCopyOfInitalCompanyPage(): DataModels.SharedMaterialsEditableCompany {
        return {
            ...this.props.initialCompany,
            companyInfo: !this.props.initialCompany.companyInfo ? {
                description: "",
                websiteUrl: ""
            } : {
                ...this.props.initialCompany.companyInfo
            }
        }
    }

    // ===============================================================================

    private getPublishButton() {
        if ((!this.state.isBeingEdited) || (!this.props.appServices.userContext?.isEditorOrMarketingRole)) {
            return null;
        }

        const buttonText = this.state.updatedCompany.companyId !== null ? this.localizeString("Re-Publish") : this.localizeString("Publish");
        return <button id="publish-page-button" className="btn btn-toxnot-primary" onClick={e => this.publishButton_click()}>{buttonText}</button>
    }

    // ===============================================================================

    protected renderConcreteComponent() {
        const disabled = !this.props.appServices.userContext?.isEditorOrMarketingRole;
        return (
            <>
                <div className="company-page-editor">
                    <div className="tn-section-row publish-page-button">
                        <div className="tn-section-col-12">
                            {this.state.publishingState === PublishingState.Publishing && <div className="alert alert-info">{this.localizeString("Updating...")}</div>}
                            {this.state.publishingState === PublishingState.Published && <div className="alert alert-success">{this.localizeString("Your Brand has been saved.")}</div>}
                            {this.renderCustomUrlStatus()}
                            <div id="publish-button-container">
                                {this.state.updatedCompany.companyId !== null && <Link role="button" id="view-my-page-button" className="btn btn-default" to={this.getUrlToExchange(this.state.updatedCompany.ownerId, this.state.updatedCompany.companyId)}>{this.localizeString("View On Exchange")}</Link>}
                                {this.getPublishButton()}
                                {" "}
                                {this.state.isBeingEdited && <button type="button" className="btn btn-default" onClick={e => this.discardButton_click()}>{this.localizeString("Discard")}</button>}
                            </div>
                        </div>
                    </div>
                    <div className="tn-section-row">
                        <div className="tn-section-col-4">
                            <div className="grouped-data">
                                <div className="group">
                                    <div className="title">{this.localizeString("Brand Logo")}</div>
                                    <div className="content">
                                        <input type="file" id="new-supplier-logo-upload" accept=".png, .jpeg, .jpg, .gif" className="form-control" onChange={e => this.companyLogoFileSelector_onChange(e)} disabled={!this.props.appServices.userContext?.isEditorOrMarketingRole} />
                                        <div className="supplier-logo-container">
                                            {(this.state.companyLogoFileObjectUrl === null) && (this.state.updatedCompany.logoFileId !== null) && <img id="supplier-logo" src={`/Api/GeneralFileImage?fileId=${this.state.updatedCompany.logoFileId}`} />}
                                            {(this.state.companyLogoFileObjectUrl !== null) && <img id="preview-supplier-logo" src={this.state.companyLogoFileObjectUrl} />}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tn-section-col-8">
                            <div className="grouped-data">
                                <div className="group">
                                    <div className="title">{this.localizeString("Info")}</div>
                                    <div className="content">
                                        <label>{this.localizeString("Brand Name")}</label>
                                        <input id={CompanyPageEditor.brandNameId} placeholder={this.localizeString("Brand Name")} type="text" className="form-control company-info-input" onChange={e => this.editing(() => this.updateCompany({ name: e.target.value }))} value={this.state.updatedCompany.name} disabled={disabled} />
                                        <label>{this.localizeString("Company Email")}</label>
                                        <input id={CompanyPageEditor.companyEmailId} placeholder={this.localizeString("Company Email")} type="text" className="form-control company-info-input" onChange={e => this.editing(() => this.updateCompany({ contactEmail: e.target.value }))} value={this.state.updatedCompany.contactEmail} disabled={disabled} />
                                        <label>{this.localizeString("Description")}</label>
                                        <textarea id="supplier-description-input" placeholder={this.localizeString("Description")} className="form-control company-info-input" rows={5} onChange={e => this.editing(() => this.companyInfo_onChange({ description: e.target.value }))} value={this.state.updatedCompany.companyInfo?.description ?? ""} disabled={disabled}></textarea>
                                        <label>{this.localizeString("Website URL")}</label>
                                        <input id="supplier-website-url-input" type="text" placeholder={this.localizeString("Website URL")} className="form-control" onChange={e => this.editing(() => this.companyInfo_onChange({ websiteUrl: e.target.value }))} value={this.state.updatedCompany.companyInfo?.websiteUrl ?? ""} disabled={disabled} />
                                    </div>
                                </div>
                                <div className="group">
                                    <div className="title">{this.localizeString("Customer Requests")} {!this.getUserContextForSignedInUser().userCapabilities.myCustomersEnabled && <UpsellProBadge navigateToUpgradePage={true} />}</div>
                                    {this.renderCustomerRequests()}
                                </div>
                                {this.state.updatedCompany.companyInfo !== null && this.state.updatedCompany.companyId !== null && (
                                    <div className="group">
                                        <div className="title">{this.localizeString("Shareable URL To Your Public Passport Library Page")}</div>
                                        <div className="content">
                                            <div className="input-group">
                                                <input id="company-link-to-copy" type="text" className="form-control" value={this.getUrlToExchange(this.state.updatedCompany.ownerId, this.state.updatedCompany.companyId, true)} disabled />
                                                <span className="input-group-btn">
                                                    <button onClick={e => Utilities.copyContentFromHTMLInputElementToClipboard(document.getElementById("company-link-to-copy") as HTMLInputElement)} className="btn btn-default copy-url-button" type="button">{this.localizeString("Copy")}</button>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <ToxnotReactModal modalId={"requirements-for-custom-url-name-modal"} localizedTitle={this.localizeString("Requirements for Customer URL")}>
                    <ToxnotReactModalBody>
                        <ul>
                            <li>{this.localizeString("Only letters, numbers, and dashes allowed (no spaces)")}</li>
                            <li>{this.localizeString("Case insensitive")}</li>
                            <li>{this.localizeString("Green: Your URL is good!")}</li>
                            <li>{this.localizeString("Red: Your URL is already in use by someone else or is not valid, please try another name.")}</li>
                        </ul>
                    </ToxnotReactModalBody>
                </ToxnotReactModal>
                <ToxnotReactModal modalId={"confirm-discard-changes-modal"} localizedTitle={this.localizeString("Discard Changes?")}>
                    <ToxnotReactModalBody>
                        <p>
                            {this.localizeString("Are you sure you want to discard your changes?") }
                        </p>
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter localizer={this.props.appServices}>
                        <button type="button" className="btn btn-toxnot-primary" data-dismiss="modal" onClick={e => this.discardConfirmButton_click()}>{this.localizeString("Yes, Discard")}</button>
                    </ToxnotReactModalFooter>
                </ToxnotReactModal>
                <PickReportsForCompanyModal reportsToRender={this.state.updatedCompany.reports} onReportsSelected={this.reports_onSelect} />
            </>
        );
    }

    // ===============================================================================

    private renderCustomUrlNameGroup() {
        const showSaveButton = (
            (this.state.customUrlNameState !== CustomUrlNameState.Disabled) &&
            this.props.appServices.userContext?.isEditorOrMarketingRole
        );

        const showCopyButton = this.state.customUrlNameState === CustomUrlNameState.Disabled;
        const showEditButton = this.state.customUrlNameState === CustomUrlNameState.Disabled && this.props.appServices.userContext?.isEditorOrMarketingRole;

        return (
            <div className="custom-url-name-group">
                <span id="custom-url-name-hostname">{Utilities.addhostnameToString(ToxnotReactComponentPaths.RequestReportsBasePath + "/")}</span>
                <input id="custom-url-name-input" type="text" className={"form-control" + this.getClassNameForCustomUrlNameInput()} value={this.state.updatedCompany.customUrlName ?? ""} onChange={this.customNameForUrlInput_onChange} onKeyDown={this.customNameForUrlInput_onKeyDown} disabled={(this.state.customUrlNameState === CustomUrlNameState.Disabled || !this.props.appServices.userContext?.isEditorOrMarketingRole)} />
                {showCopyButton && <button type="button" className="btn btn-default" onClick={e => this.copyCustomerRequestsUrlToClipboardButton_click()}>{this.localizeString("Copy")}</button>}
                {showEditButton && <button id="edit-customer-url-name-button" type="button" className="btn btn-default" onClick={e => this.editCustomerUrl_click()}>{this.localizeString("Edit")}</button>}
                {showSaveButton && <button id="save-customer-url-name-button" type="button" className="btn btn-default" onClick={e => this.publishButton_click()}>{this.localizeString("Save")}</button>}
            </div>
        );
    }

    // ===============================================================================

    private renderCustomerRequests() {
        let body: JSX.Element;

        const reportTypesAndRegulations = this.getUserContextForSignedInUser().reportTypesAndRegulations;

        const sortedReportTypes = ReportTypesManager.getSortedListOfReportTypesOrRegulations(this.state.updatedCompany.reports, this.props.appServices, reportTypesAndRegulations.dynRegsForUser);

        if (this.getUserCapabilities().myCustomersEnabled) {
            body = (
                <div className="content">
                    <label>{this.localizeString("URL")}</label>
                    <p>
                        {this.localizeString("You can send this link to your customers, where they'll be able to fill out requests for reports from you.")} <a role="button" onClick={e => $('#requirements-for-custom-url-name-modal').modal('show')}>{this.localizeString("Requirements")}</a>
                    </p>
                    {this.renderCustomUrlNameGroup()}
                    <div className="reports-to-select">
                        <label>{this.localizeString("Reports your customers can request")}</label>
                        {" "}
                        <a role="button" onClick={e => this.editReports_click()}>{this.localizeString("Edit")}</a>
                        <ul>
                            {sortedReportTypes.map((r, i) => (
                                <li key={i}>{ReportTypesManager.getFriendlyNameForReportTypeOrRegulation(r, this.props.appServices, reportTypesAndRegulations.dynRegsForUser)}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            );
        }
        else {
            body = (
                <div className="content">
                    <div className="alert alert-info">
                        <p>
                            {this.localizeString("With 3E Exchange Enterprise, you can send a branded URL to customers where they can request reports from you. 3E Exchange can then help you manage your requests & automatically generate reports for your customers.")} <a href="/Account/Upgrade">{this.localizeString("Click here")}</a> {this.localizeString("to Learn More & Upgrade.") }
                        </p>
                    </div>
                </div>
            );
        }

        return body;
    }

    // ===============================================================================

    private static getUpdateCompanyRequest(updatedCompany: DataModels.SharedMaterialsEditableCompany): UpdateCompanyRequest {
        return {
            name: updatedCompany.name,
            logoFileId: updatedCompany.logoFileId,
            contactEmail: updatedCompany.contactEmail,
            customUrlName: updatedCompany.customUrlName,
            companyInfo: updatedCompany.companyInfo,
            reports: updatedCompany.reports,
            companyId: updatedCompany.companyId
        }
    }

    // ===============================================================================

    /**
     * Verifies that the inputs and state are consistent with sending an update.
     * 
     * Returns a bundle with updated company state (with text fields trimmed) along
     * with a potential error message and selector to bring into focus.
     */
    private verifyCompany(company: DataModels.SharedMaterialsEditableCompany): VerificationBundle {
        const bundle: VerificationBundle = {
            updatedCompany: { ...company },
            localizedErrorMsg: null,
            focusSelectorId: null
        }

        // trim all text field values
        bundle.updatedCompany.name = bundle.updatedCompany.name.trim();
        bundle.updatedCompany.contactEmail = bundle.updatedCompany.contactEmail.trim();
        const companyInfo = bundle.updatedCompany.companyInfo ?? null;
        if (null != companyInfo) {
            companyInfo.description = companyInfo.description.trim();
            companyInfo.websiteUrl = companyInfo.websiteUrl.trim();
        }

        if (bundle.updatedCompany.name === "") {
            bundle.localizedErrorMsg = this.localizeString("Please enter your company name.");
            bundle.focusSelectorId = CompanyPageEditor.brandNameId;
        }
        else if (bundle.updatedCompany.contactEmail === "") {
            bundle.localizedErrorMsg = this.localizeString("Please enter your contact email.");
            bundle.focusSelectorId = CompanyPageEditor.companyEmailId;
        }
        else if (!Utilities.isValidEmailAddress(bundle.updatedCompany.contactEmail)) {
            bundle.localizedErrorMsg = this.localizeString("Please enter a valid contact email.");
            bundle.focusSelectorId = CompanyPageEditor.companyEmailId;
        }
        else if (this.state.customUrlNameState === CustomUrlNameState.Checking ||
            this.state.customUrlNameState === CustomUrlNameState.Invalid) {
            bundle.localizedErrorMsg = this.localizeString("Customer URL was not valid.");
        }

        return bundle;
    }

    // ===============================================================================

    private async publishCompanyPage() {
        const bundle = this.verifyCompany(this.state.updatedCompany);

        if (bundle.localizedErrorMsg !== null) {
            alert(bundle.localizedErrorMsg);

            // update any trimmed values
            this.setState(
                {
                    updatedCompany: {
                        ...bundle.updatedCompany,                    
                    }
                },
                () => { 
                    // callback in case focus should be set
                    if (null !== bundle.focusSelectorId) {
                        $("#" + bundle.focusSelectorId).focus() 
                    } 
                }
            );
            return;
        }

        // If already publishing, don't start a new publish.
        if (this.state.publishingState === PublishingState.Publishing) {
            return;
        }

        const updateCompanyRequest = CompanyPageEditor.getUpdateCompanyRequest(bundle.updatedCompany);

        if (updateCompanyRequest === null) {
            return;
        }

        this.setState({
            publishingState: PublishingState.Publishing
        });

        if (this.state.companyLogoFile !== null) {
            const fileApiResult = await this._serviceClient.uploadGeneralImageFile(
                bundle.updatedCompany.name,
                "",
                0, // type
                false, // isSupplierUpload
                true, // isPublic
                this.state.companyLogoFile
            );

            if (!fileApiResult.success || fileApiResult.data === null) {
                this.props.onCompanyPagePublish(this.props.initialCompany, false);
                return;
            }

            updateCompanyRequest.logoFileId = fileApiResult.data.fileId;
        }

        const publishApiResult = await this._serviceClient.updateSharedMaterialsCompany(updateCompanyRequest);

        if (!publishApiResult.success || publishApiResult.data === null) {
            this.props.onCompanyPagePublish(this.props.initialCompany, false);
            return;
        }

        this.setState({
            isBeingEdited: false,
            publishingState: PublishingState.Published,
            customUrlNameState: CustomUrlNameState.Disabled,
            updatedCompany: {
                ...bundle.updatedCompany,
                companyId: publishApiResult.data
            }
        });

        this.props.onCompanyPagePublish(bundle.updatedCompany, true);
    }

    // ===============================================================================

    private getClassNameForCustomUrlNameInput() {
        switch (this.state.customUrlNameState) {
            case (CustomUrlNameState.Valid):
                return " valid";

            case (CustomUrlNameState.Invalid):
                return " invalid";

            default:
                return "";
        }
    }

    // ===============================================================================

    private renderCustomUrlStatus() {
        return (
            this.getUserContextForSignedInUser().userCapabilities.myCustomersEnabled &&
            this.state.updatedCompany.customUrlName === null &&
            (this.state.customUrlNameState === CustomUrlNameState.Disabled) &&
            (this.state.updatedCompany.companyId !== null) && 
            <div className="alert alert-warning">{this.localizeString("Looks like you haven't set up your customer request url yet.") + " "}<a role="button" onClick={e => this.editCustomerUrl_click()}>{this.localizeString("Click here to start")}</a></div>
        );
    }

    // ===============================================================================

    private setIsBeingEditedState() {
        this.props.onCompanyBeingEdited(true);

        this.setState({
            isBeingEdited: true,
            publishingState: PublishingState.Default
        });
    }

    // ===============================================================================

    private editing(editOperation: () => void) {
        this.setIsBeingEditedState();
        editOperation();
    }

    // ===============================================================================

    private cleanUpLogoObjectUrl() {
        if (this.state.companyLogoFileObjectUrl !== null) {
            URL.revokeObjectURL(this.state.companyLogoFileObjectUrl);
        }
    }

    // ===============================================================================
    //
    // UI Events
    //
    // ===============================================================================

    private companyLogoFileSelector_onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if ((e.target.files === null)|| (e.target.files.length === 0)) {
            return;
        }

        const file = e.target.files[0];

         // Validate file extension (case-insensitive) and MIME type
        const allowedExtensions = ['png', 'jpeg', 'jpg', 'gif'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const allowedMimeTypes = ['image/png', 'image/jpeg', 'image/gif'];

        if (
            !fileExtension || 
            !allowedExtensions.includes(fileExtension) || 
            !allowedMimeTypes.includes(file.type)
        ) {
            alert('Invalid file type selected. Please upload a valid image.');
            return;
        }

        this.cleanUpLogoObjectUrl();

        const objectUrl = URL.createObjectURL(file);

        this.setIsBeingEditedState();

        this.setState({
            companyLogoFileObjectUrl: objectUrl,
            companyLogoFile: file,
        });
    }

    // ===============================================================================

    private publishButton_click = () => {
        this.publishCompanyPage();
    }

    // ===============================================================================

    private customNameForUrlInput_onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!this.testCustomUrlName(e.key)) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
    }

    // ===============================================================================

    private updateCompany(updatedCompany: Partial<DataModels.SharedMaterialsEditableCompany>) {
        this.setState({
            updatedCompany: {
                ...this.state.updatedCompany,
                ...updatedCompany
            }
        });
    }

    // ===============================================================================

    /**
     * returns a true if the input matches the regex /^[a-zA-Z0-9_\-]*$/ 
     * else returns false.
     */
    private testCustomUrlName(input: string) {
        // regex
        const pattern = /^[a-zA-Z0-9_\-]*$/;
        return pattern.test(input);
    }

    // ===============================================================================

    private customNameForUrlInput_onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const customUrlName = $("#custom-url-name-input").val()?.toString() ?? "";

        this.setIsBeingEditedState();

        this.updateCompany({ customUrlName: customUrlName });

        if (!this.testCustomUrlName(customUrlName)) {
            this.setState({
                customUrlNameState: CustomUrlNameState.Invalid
            });
        }
        else {
            this.setState({
                customUrlNameState: CustomUrlNameState.Checking
            });

            const apiResult = await this._serviceClient.checkIfCompanyCustomUrlIsValid(this.state.updatedCompany.companyId, customUrlName);

            if (!apiResult.success || apiResult.data === null) {
                this.props.appServices.displayApiErrorMessage();
                return;
            }

            // Someone else has this name
            if (!apiResult.data) {
                this.setState({
                    customUrlNameState: CustomUrlNameState.Invalid
                });

            }
            else {
                this.setState({
                    customUrlNameState: CustomUrlNameState.Valid
                });
            }
        }
    }

    // ===============================================================================

    private copyCustomerRequestsUrlToClipboardButton_click = () => {
        const companyId = $("#custom-url-name-input").val()?.toString() ?? "";

        const urlToCopy = (Utilities.addhostnameToString(ToxnotReactComponentPaths.RequestReportsBasePath + "/")) + companyId;

        navigator.clipboard.writeText(urlToCopy);
    }

    // ===============================================================================

    private editCustomerUrl_click = () => {
        this.setIsBeingEditedState();

        this.setState({
            customUrlNameState: CustomUrlNameState.OpenToEdit,
            publishingState: PublishingState.Default
        },
            () => $("#custom-url-name-input").focus() // callback
        ); 
    }

    // ===============================================================================

    private editReports_click = () => {
        $('#pick-reports-for-company-modal').modal('show');
    }

    // ===============================================================================

    private companyInfo_onChange = (companyInfo: Partial<DataModels.CompanyInfo>) => {
        if (this.state.updatedCompany.companyInfo === null) {
            return; 
        }

        this.setIsBeingEditedState();

        this.updateCompany({
            companyInfo: {
                ...this.state.updatedCompany.companyInfo,
                ...companyInfo
            }
        });
    }

    // ===============================================================================

    private reports_onSelect = (selectedReports: { [reportType: string]: ReportContext | null }) => {
        this.setIsBeingEditedState();
        this.updateCompany({ reports: Object.keys(selectedReports) });
    }

    // ===============================================================================

    private discardButton_click = () => {
        $('#confirm-discard-changes-modal').modal('show');
    }

    // ===============================================================================

    private discardConfirmButton_click = () => {
        this.setState({
            updatedCompany: this.getCopyOfInitalCompanyPage(),
            isBeingEdited: false,
            customUrlNameState: CustomUrlNameState.Disabled,
            companyLogoFileObjectUrl: null,
            companyLogoFile: null
        });

        this.props.onCompanyBeingEdited(false);
    }

    // ===============================================================================

    private static readonly brandNameId = "supplier-name-input";
    private static readonly companyEmailId = "supplier-email-input";
}

// ===============================================================================

export default connect(
    (state: ApplicationState, ownProps: CompanyPageEditorOwnProps) => ({ ...state.sharedMaterialsManagementState, appServices: state.appServices }),
)(CompanyPageEditor as any);
